/**
 * Applying a mirror to a painted stroke.
 *
 * The map in `mirror-map.ts` says which texel is which texel's reflection. This
 * says what to do with that while someone is drawing, and the two decisions
 * that are easy to get wrong are both here.
 *
 * See docs/decisions/mirror-and-editor-tools.md §2.
 */
import { REGION_IDS, type RegionId } from "./format";
import { type MirrorMap, mirrorOf } from "./mirror-map";
import { REGIONS } from "./regions";

/** The shape both the canvas and the wet layer come in. */
export interface Pixels {
  width: number;
  height: number;
  /** RGBA, 4 bytes per texel, row-major. */
  data: Uint8ClampedArray;
  /**
   * Where this buffer's top-left sits on the canvas, when it is a sub-rect.
   *
   * Reading the whole 585x559 canvas per stroke was costing four full
   * `getImageData` calls — each one a GPU-to-CPU sync — and it was the lag on
   * every mirrored stroke. Everything here indexes in CANVAS coordinates and
   * subtracts this, so a caller can hand over just the rect it cares about.
   */
  originX?: number;
  originY?: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Smallest rect containing both, or whichever is non-null. */
export function unionRect(a: Rect | null, b: Rect | null): Rect | null {
  if (!a) return b;
  if (!b) return a;
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    w: Math.max(a.x + a.w, b.x + b.w) - x,
    h: Math.max(a.y + a.h, b.y + b.h) - y,
  };
}

/**
 * A rect big enough to hold both a stroke and wherever its mirror will land.
 *
 * Undo needs this BEFORE the stroke is composited, so the mirror's real extent
 * is not known yet. Sampling the map across the stroke's own rect gives it:
 * every partner of every texel sampled, unioned with the rect itself.
 *
 * Sampled rather than exhaustive, because the map is smooth within a region and
 * a stroke's rect can be large. A rect slightly too big costs a wider undo
 * readback; one slightly too small silently loses half a stroke on undo, so the
 * result is padded by the sampling step before being clamped.
 */
export function mirrorBoundsFor(r: Rect, mirror: MirrorMap, step = 4): Rect {
  let x0 = r.x;
  let y0 = r.y;
  let x1 = r.x + r.w;
  let y1 = r.y + r.h;
  for (let y = r.y; y < r.y + r.h; y += step) {
    for (let x = r.x; x < r.x + r.w; x += step) {
      if (x < 0 || y < 0 || x >= mirror.width || y >= mirror.height) continue;
      const to = mirror.partner[y * mirror.width + x] ?? -1;
      if (to < 0) continue;
      const tx = to % mirror.width;
      const ty = (to - tx) / mirror.width;
      if (tx < x0) x0 = tx;
      if (ty < y0) y0 = ty;
      if (tx + 1 > x1) x1 = tx + 1;
      if (ty + 1 > y1) y1 = ty + 1;
    }
  }
  x0 = Math.max(0, x0 - step);
  y0 = Math.max(0, y0 - step);
  x1 = Math.min(mirror.width, x1 + step);
  y1 = Math.min(mirror.height, y1 + step);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Copy a stroke onto its mirror.
 *
 * `target` is the canvas AFTER the stroke has been composited, and `coverage`
 * is the wet layer — the stroke's own alpha, which is what says where the
 * stroke actually went.
 *
 * **Driven by coverage, not by the rect.** A stroke's bounding box is mostly
 * not the stroke: a diagonal line fills a fraction of its box, and every other
 * texel in there is older artwork that happens to be nearby. Mirroring the
 * whole rect would copy that artwork onto the far limb, which looks exactly
 * like corruption because it is.
 *
 * **The RESULT is mirrored, not the operation.** Replaying the stroke on the
 * other side would composite it twice wherever the two halves meet, so a
 * half-opaque brush drawn across the chest would come out darker down the
 * middle than at either edge. Copying finished texels cannot do that.
 *
 * Returns the rect that was written, so the caller can mark it dirty and hand
 * it to undo. Null when the stroke had no mirrorable coverage at all.
 */
export interface MirrorWriteOptions {
  /**
   * Whether the mirror may write this texel. Everything is allowed when omitted.
   *
   * **A mirror is not a way around the face selection.** `paintableMask` clips
   * the stroke's own outline and `landsHere` filters what carries across a
   * fold, and both of those exist because a stroke must not reach a face
   * somebody excluded. The mirror walked straight past both: measured, a stroke
   * confined to `torso.front` put all 564 of its texels onto `torso.back` with
   * the back deselected.
   *
   * A predicate rather than a `Selection`, so this file keeps knowing nothing
   * about faces — it is about geometry, and which faces are painted on is a
   * question for the editor. It is also what makes the check cheap: the caller
   * hands over a closure that already early-outs when no selection is set,
   * which is almost always.
   */
  allow?: (x: number, y: number) => boolean;
}

/** Byte offset of a canvas texel inside a (possibly offset) buffer, or -1. */
function at(p: Pixels, x: number, y: number): number {
  const lx = x - (p.originX ?? 0);
  const ly = y - (p.originY ?? 0);
  if (lx < 0 || ly < 0 || lx >= p.width || ly >= p.height) return -1;
  return (ly * p.width + lx) * 4;
}

export function mirrorStroke(
  target: Pixels,
  coverage: Pixels,
  rect: Rect,
  mirror: MirrorMap,
  opts: MirrorWriteOptions = {},
): Rect | null {
  const width = mirror.width;
  const height = mirror.height;
  const x0 = Math.max(0, Math.floor(rect.x));
  const y0 = Math.max(0, Math.floor(rect.y));
  const x1 = Math.min(width, Math.ceil(rect.x + rect.w));
  const y1 = Math.min(height, Math.ceil(rect.y + rect.h));
  if (x1 <= x0 || y1 <= y0) return null;

  /*
    Read every source value before writing any of them.

    A stroke that crosses the plane of symmetry has its own mirror inside its
    own rect, so writing in place would let a texel already overwritten by the
    mirror become the source for the next one, and the stroke would smear along
    the centreline.
  */
  const touched: number[] = [];
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const c = at(coverage, x, y);
      if (c < 0 || (coverage.data[c + 3] as number) === 0) continue;
      touched.push(y * width + x);
    }
  }
  if (touched.length === 0) return null;

  const covered = new Set(touched);
  const snapshot = new Uint8ClampedArray(touched.length * 4);
  for (let k = 0; k < touched.length; k++) {
    const t = touched[k] as number;
    const i = at(target, t % width, (t - (t % width)) / width);
    if (i < 0) continue;
    snapshot[k * 4] = target.data[i] as number;
    snapshot[k * 4 + 1] = target.data[i + 1] as number;
    snapshot[k * 4 + 2] = target.data[i + 2] as number;
    snapshot[k * 4 + 3] = target.data[i + 3] as number;
  }

  let wx0 = Number.POSITIVE_INFINITY;
  let wy0 = Number.POSITIVE_INFINITY;
  let wx1 = Number.NEGATIVE_INFINITY;
  let wy1 = Number.NEGATIVE_INFINITY;

  for (let k = 0; k < touched.length; k++) {
    const from = touched[k] as number;
    const to = mirror.partner[from] ?? -1;
    if (to < 0) continue;
    /*
      Where a stroke covers BOTH halves of a pair, one of them has to win or
      the two writes just exchange the halves and nothing becomes symmetric.
      The lower index wins: arbitrary between the two, but stable, and it makes
      the operation idempotent — mirroring twice gives the same canvas, which is
      what stops a held-down brush from oscillating.
    */
    if (covered.has(to) && to < from) continue;
    const tx0 = to % width;
    const ty0 = (to - tx0) / width;
    // The far side may be a face the user excluded. See MirrorWriteOptions.
    if (opts.allow && !opts.allow(tx0, ty0)) continue;
    const o = at(target, tx0, ty0);
    if (o < 0) continue;
    target.data[o] = snapshot[k * 4] as number;
    target.data[o + 1] = snapshot[k * 4 + 1] as number;
    target.data[o + 2] = snapshot[k * 4 + 2] as number;
    target.data[o + 3] = snapshot[k * 4 + 3] as number;

    const tx = to % width;
    const ty = (to - tx) / width;
    if (tx < wx0) wx0 = tx;
    if (ty < wy0) wy0 = ty;
    if (tx > wx1) wx1 = tx;
    if (ty > wy1) wy1 = ty;
  }

  if (wx1 < wx0) return null;
  return { x: wx0, y: wy0, w: wx1 - wx0 + 1, h: wy1 - wy0 + 1 };
}

/** A half of the body, named in the character's own frame. */
export type BodySide = "left" | "right";

/**
 * Lift one rect's pixels out of a full-canvas buffer.
 *
 * Bytes rather than an `ImageData`, so this can be checked without a browser.
 * It is four lines of stride arithmetic and it is exactly the four lines that
 * produce a sheared image if `width` is ever the rect's rather than the
 * canvas's — a bug that shows up only when somebody presses undo, long after
 * the code that caused it went in.
 */
export function cropBytes(
  data: Uint8ClampedArray,
  width: number,
  rect: Rect,
): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(rect.w * rect.h * 4);
  for (let y = 0; y < rect.h; y++) {
    const from = ((rect.y + y) * width + rect.x) * 4;
    out.set(data.subarray(from, from + rect.w * 4), y * rect.w * 4);
  }
  return out;
}

/** Whether this map knows its halves. False disables the one-shot commands. */
export function hasSides(mirror: MirrorMap): boolean {
  for (let i = 0; i < mirror.side.length; i++) if (mirror.side[i] !== 0) return true;
  return false;
}

/**
 * Overwrite one half of a layer with the reflection of the other.
 *
 * Live symmetry only helps somebody who turned it on before they started. This
 * is for everyone else: the jacket is drawn down one side, and the other side is
 * the two hours they were dreading.
 *
 * **Transparent texels copy too.** The instinct is to skip them — copying
 * "nothing" feels like it should be a no-op — but the result of that is not
 * symmetric, it is the old right half showing through the gaps in the new one.
 * A hole in the source is part of the source. What the operation promises is
 * that afterwards the two halves match, and skipping alpha-0 breaks exactly
 * that promise in the places people notice least until export.
 *
 * Nothing on the plane itself moves: those texels are their own reflection, and
 * the halves meet there rather than one owning it.
 *
 * Returns the rect written, for the caller's dirty rect and undo entry, or null
 * when this half had nothing to give.
 */
export function mirrorHalf(
  target: Pixels,
  mirror: MirrorMap,
  from: BodySide,
  opts: MirrorWriteOptions = {},
): Rect | null {
  const width = mirror.width;
  const want = from === "left" ? 1 : -1;

  const sources: number[] = [];
  for (let i = 0; i < mirror.side.length; i++) {
    if (mirror.side[i] !== want) continue;
    if ((mirror.partner[i] ?? -1) < 0) continue;
    sources.push(i);
  }
  if (sources.length === 0) return null;

  /*
    Read before writing, as in `mirrorStroke` and for the same reason.

    Here the two sets should be disjoint by construction — every source is on
    one half and every destination on the other — but "should be" is doing work
    it has not earned: the sealed ring and the fitted transforms both write
    partners the reflection did not produce, and a single same-side pair would
    turn this into a smear that only shows up on one body. Half a megabyte to
    make the operation order-independent is not a trade worth thinking about.
  */
  const snapshot = new Uint8ClampedArray(sources.length * 4);
  for (let k = 0; k < sources.length; k++) {
    const s = sources[k] as number;
    const i = at(target, s % width, (s - (s % width)) / width);
    if (i < 0) continue;
    snapshot[k * 4] = target.data[i] as number;
    snapshot[k * 4 + 1] = target.data[i + 1] as number;
    snapshot[k * 4 + 2] = target.data[i + 2] as number;
    snapshot[k * 4 + 3] = target.data[i + 3] as number;
  }

  let wx0 = Number.POSITIVE_INFINITY;
  let wy0 = Number.POSITIVE_INFINITY;
  let wx1 = Number.NEGATIVE_INFINITY;
  let wy1 = Number.NEGATIVE_INFINITY;

  for (let k = 0; k < sources.length; k++) {
    const to = mirror.partner[sources[k] as number] as number;
    const tx = to % width;
    const ty = (to - tx) / width;
    /*
      Scoped to the selection when there is one — which is what makes this
      "mirror selection" rather than a second command.

      The plan listed mirror-selection as a separate item blocked on a selection
      existing. Now that one does, it is not a separate item: a command that
      acts on the selection when there is one and the whole layer when there is
      not is the convention every editor already has, and it needs no new
      button to explain.
    */
    if (opts.allow && !opts.allow(tx, ty)) continue;
    const o = at(target, tx, ty);
    if (o < 0) continue;
    target.data[o] = snapshot[k * 4] as number;
    target.data[o + 1] = snapshot[k * 4 + 1] as number;
    target.data[o + 2] = snapshot[k * 4 + 2] as number;
    target.data[o + 3] = snapshot[k * 4 + 3] as number;
    if (tx < wx0) wx0 = tx;
    if (ty < wy0) wy0 = ty;
    if (tx > wx1) wx1 = tx;
    if (ty > wy1) wy1 = ty;
  }

  if (wx1 < wx0) return null;
  return { x: wx0, y: wy0, w: wx1 - wx0 + 1, h: wy1 - wy0 + 1 };
}

/**
 * The regions a half of the body occupies.
 *
 * The command's labels say "left" and "right", and in the 3D preview the
 * character faces you, so its left is on your right — a word that is correct and
 * still misread. Highlighting the template regions about to be overwritten says
 * it in a way there is no arguing with, before anything is committed.
 */
export function sideRegions(mirror: MirrorMap, side: BodySide): RegionId[] {
  const want = side === "left" ? 1 : -1;
  const hit: RegionId[] = [];
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    let found = false;
    for (let y = r.y; y < r.y + r.h && !found; y++) {
      const row = y * mirror.width;
      for (let x = r.x; x < r.x + r.w; x++) {
        if (mirror.side[row + x] === want) {
          found = true;
          break;
        }
      }
    }
    if (found) hit.push(id);
  }
  return hit;
}

/**
 * Which region a region mirrors onto, read off the map.
 *
 * The guide uses this to show a pairing before anyone commits a stroke, and it
 * is the same question the whole feature turns on: hovering `rightLimb.front`
 * should light up `leftLimb.front`, not `leftLimb.back`. Derived rather than
 * declared, for the reason the map itself is — nobody should be maintaining a
 * table of the strip rotation.
 *
 * Sampled at a few interior points rather than one. A single sample can land on
 * seal fill with no partner, and a region whose centre happens to be unpaired
 * would otherwise look like it has no mirror at all.
 */
export function pairedRegion(region: RegionId, mirror: MirrorMap): RegionId | null {
  const r = REGIONS[region];
  for (const [dx, dy] of [
    [0.5, 0.5],
    [0.35, 0.5],
    [0.65, 0.5],
    [0.5, 0.35],
  ] as const) {
    const p = mirrorOf(mirror, Math.round(r.x + r.w * dx), Math.round(r.y + r.h * dy));
    if (!p) continue;
    for (const id of REGION_IDS) {
      const q = REGIONS[id];
      if (p.x >= q.x && p.x < q.x + q.w && p.y >= q.y && p.y < q.y + q.h) return id;
    }
  }
  return null;
}
